import { Box, Stack } from '@mui/material'
import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  Heading3,
  Heading4,
} from '@front/components/data-display/typography/Typography'
import { Dialog } from '@front/components/feedback/dialog/Dialog'
import { FilterChipSquarish } from '@front/components/inputs/filter-chip-squarish/FilterChipSquarish'
import { Separator } from '@front/components/layout/separator/Separator'
import { useMoreFiltersSelection } from '../hooks/useMoreFiltersSelection'
import { contractTypes } from '../../constants/contractTypes'
import { capitalizeFirst } from '@hublo/utils/string'

type PropsType = {
  isOpen: boolean
  onClose: () => void
}

type FilterItemsType = {
  itemId: string
  label: string
  selected: boolean
  'data-testid': string
}

type FilterHookType = {
  handleItemClick: (itemValue: string) => void
  localValue: string | string[] | undefined
  saveValue: () => void
  resetLocalValue: () => void
}

type FilterSectionPropsType = {
  title: string
  filterItems: FilterItemsType[]
  filterHook: FilterHookType
}

export const MoreFiltersDialog = ({ isOpen, onClose }: PropsType) => {
  const { t } = useTranslation(['main.common', 'main.worker'])
  const {
    contractTypeHook,
    contractDurationHook,
    yearsOfExperienceHook,
    // institutionTypeHook,
    resetAllLocalValues,
    saveAllValues,
  } = useMoreFiltersSelection()

  const contractTypeItems = (() => {
    return contractTypes.map((el) => ({
      itemId: el,
      label: t(
        `main.worker:job_offers.filter.contract_type.${el.toLowerCase()}` as ParseKeys,
      ),
      selected: Boolean(contractTypeHook.localValue?.includes(el)),
      'data-testid': `JobAds__MoreFiltersDialog__option_${el.toLowerCase()}`,
    }))
  })()

  const contractDurationItems = (() => {
    const getLabel = (index: number) => {
      return t(
        `main.worker:job_offers.filter.contract_duration.duration_${index}` as ParseKeys,
      )
    }

    const pool = [
      { id: 'LESS_THAN_ONE_MONTH', label: getLabel(1) },
      { id: 'ONE_TO_THREE_MONTHS', label: getLabel(2) },
      { id: 'THREE_TO_SIX_MONTHS', label: getLabel(3) },
      { id: 'SIX_TO_TWELVE_MONTHS', label: getLabel(4) },
      { id: 'ONE_TO_TWO_YEARS', label: getLabel(5) },
      { id: 'MORE_THAN_TWO_YEARS', label: getLabel(6) },
    ]

    return pool.map((el) => ({
      itemId: el.id,
      label: el.label,
      selected: Boolean(contractDurationHook.localValue?.includes(el.id)),
      'data-testid': `JobAds__MoreFiltersDialog__contract_duration_option_${el.id}`,
    }))
  })()

  const yearsOfExperienceItems = (() => {
    const getLabel = (index: number) => {
      return t(
        `main.worker:job_offers.filter.years_of_xp.slot_${index}` as ParseKeys,
      )
    }

    const pool = [
      { id: 'zero_to_one', label: getLabel(1) },
      { id: 'one_to_three', label: getLabel(2) },
      { id: 'three_to_five', label: getLabel(3) },
      { id: 'five_to_ten', label: getLabel(4) },
      { id: 'more_than_ten', label: getLabel(5) },
    ]

    return pool.map((el) => ({
      itemId: el.id,
      label: el.label,
      selected: yearsOfExperienceHook.localValue === el.id,
      'data-testid': `JobAds__MoreFiltersDialog__years_of_experience_option_${el.id}`,
    }))
  })()

  /**@ImportantNote */
  /**
   * The institutionType filter has been deactivated for now
   * as another team is still implementing the required endpoints.
   */

  // const institutionTypeItems = (() => {
  //   const getLabel = (index: number) => {
  //     return t(
  //       `main.worker:job_offers.filter.institution_type.type_${index}` as ParseKeys,
  //     )
  //   }

  //   const pool = [
  //     { id: 'type_1', label: getLabel(1) },
  //     { id: 'type_2', label: getLabel(2) },
  //     { id: 'type_3', label: getLabel(3) },
  //     { id: 'type_4', label: getLabel(4) },
  //     { id: 'type_5', label: getLabel(5) },
  //     { id: 'type_6', label: getLabel(6) },
  //     { id: 'type_7', label: getLabel(7) },
  //     { id: 'type_8', label: getLabel(8) },
  //   ]

  //   return pool.map((el) => {
  //     return {
  //       itemId: el.id,
  //       label: el.label,
  //       selected: Boolean(institutionTypeHook.localValue?.includes(el.id)),
  //       'data-testid': `JobAds__MoreFiltersDialog__institution_type_option_${el.id}`,
  //     }
  //   })
  // })()

  const handleCancel = () => {
    resetAllLocalValues()
    onClose()
  }

  const handleValidate = () => {
    saveAllValues()
    onClose()
  }

  const FiltersSection = ({
    title,
    filterItems,
    filterHook,
  }: FilterSectionPropsType) => {
    return (
      <>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Heading4>{title}</Heading4>
          <Stack
            direction="row"
            sx={{
              gap: 1.5,
              flex: 1,
              flexWrap: 'wrap',
            }}
          >
            {filterItems.map((item) => (
              <FilterChipSquarish
                key={item.itemId}
                label={item.label}
                selected={item.selected}
                onClick={() => {
                  filterHook.handleItemClick(item.itemId)
                }}
              />
            ))}
          </Stack>
        </Stack>
        <Separator size="L" />
      </>
    )
  }

  return (
    <Dialog
      bottomActions={{
        secondaryButtonProps: {
          variant: 'secondary',
          label: capitalizeFirst(t('main.common:cancel')),
          onClick: handleCancel,
          'data-testid': 'JobAds__MoreFiltersDialog__btn_cancel',
        },
        primaryButtonProps: {
          variant: 'primary',
          label: capitalizeFirst(t('main.common:validate')),
          onClick: handleValidate,
          'data-testid': 'JobAds__MoreFiltersDialog__btn_validate',
        },
      }}
      content={
        <Box
          sx={{
            mx: 2,
          }}
        >
          <FiltersSection
            title={t('main.worker:shift_details.contract_type')}
            filterItems={contractTypeItems}
            filterHook={contractTypeHook}
          />
          <FiltersSection
            title={t('main.worker:job_offers.filter.contract_duration.title')}
            filterItems={contractDurationItems}
            filterHook={contractDurationHook}
          />
          <FiltersSection
            title={t('main.worker:job_offers.filter.years_of_xp.title')}
            filterItems={yearsOfExperienceItems}
            filterHook={yearsOfExperienceHook}
          />
          {/**@ImportantNote */}
          {/**
           * The institutionType filter has been deactivated for now
           * as another team is still implementing the required endpoints.
           */}
          {/* <FiltersSection
            title={t('main.worker:job_offers.filter.institution_type.title')}
            filterItems={institutionTypeItems}
            filterHook={institutionTypeHook}
          /> */}
        </Box>
      }
      data-testid="JobAds__MoreFiltersDialog"
      isOpen={isOpen}
      onClose={handleCancel}
      title={
        <Heading3>
          {t('main.worker:job_offers.filter.more_filters_title')}
        </Heading3>
      }
      variant="fullScreenOnMobile"
      withGutters={false}
    />
  )
}
